import type React from 'react';
import { useEffect } from 'react';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ControlledSwitch } from 'components/ControlledComponents/ControlledSwitch';
import { useForm } from 'react-hook-form';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { useGetDocumentPreviewEmailVisibility } from 'hooks/useGetDocumentPreviewEmailVisibility';
import { useUpdateDocumentPreviewPermissions } from 'hooks/useUpdateDocumentPreviewPermissions';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';

interface ViewerIdentificationTabProps {
  onBack: VoidFunction;
  contentId: string;
}

export const ViewerIdentificationTab: React.FC<ViewerIdentificationTabProps> = ({ onBack, contentId }) => {
  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const documentId = documentData?.documentId as string;
  const { previewEmailVisibility, isLoading } = useGetDocumentPreviewEmailVisibility({ documentId, contentId });
  const { updatePreviewEmailVisibility } = useUpdateDocumentPreviewPermissions({ documentId, contentId });

  const { t } = useTranslation();
  const formMethods = useForm({
    defaultValues: {
      has_email_visibility: false,
    },
  });

  useEffect(() => {
    if (previewEmailVisibility) {
      formMethods.setValue('has_email_visibility', previewEmailVisibility.has_email_visibility);
    }
  }, [previewEmailVisibility, formMethods]);

  const handleOnChangeEmail = (value: boolean) => {
    updatePreviewEmailVisibility({
      has_email_visibility: value,
    });
  };

  return (
    <CardContent sx={{ maxWidth: '380px' }} data-testid="viewer-identification-tab">
      <Stack spacing={0.5} mb={1} direction="row" alignItems="center">
        <IconButton onClick={onBack}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h5" fontSize="24px" fontWeight={600} color="text.primary">
          {t('share_document_v3.viewer_identification_card.title')}
        </Typography>
      </Stack>
      <Typography variant="body1" fontSize="16px" fontWeight={400} color="text.secondary" mb={1.5}>
        {t('share_document_v3.viewer_identification_card.text')}
      </Typography>
      <Stack direction="row" justifyContent="space-between" gap={2} mb={1} alignItems="center">
        <Typography variant="body1" fontSize="16px" fontWeight={400} color="text.primary">
          {t('share_document_v3.viewer_identification_card.email')}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" width={100} height={32} />
        ) : (
          <ControlledSwitch name="has_email_visibility" control={formMethods.control} handleOnChange={handleOnChangeEmail} />
        )}
      </Stack>
    </CardContent>
  );
};
